/**
 * Translates the `growthRecommendations` selection of `GSNForContentSidebarEntryPoint`
 * into the discriminated `InvitedAppShortcutsRecommendationsResult` consumed by
 * `GSNForContentSidebarWithInvitedAppRecommendations`.
 *
 * The recommendations are fetched as part of the main GSN query (behind
 * `@include(if: $shouldFetchInvitedAppShortcuts)`) rather than a separate
 * query/entrypoint, so this reads the field off the already-loaded fragment
 * data. The destructure-then-rebuild mirrors `InvitedAppShortcutsRecommendationsResponse`
 * in `cross-join-nudges`, which is the single source of truth for the translator.
 */
import {
	buildInvitedAppShortcutsRecommendationsResult,
	type InvitedAppShortcutsRecommendationsResponse,
	type InvitedAppShortcutsRecommendationsResult,
} from '@atlassian/cross-join-nudges/ui/invited-app-shortcuts/invited-app-shortcuts-recommendations-provider';
import type { GSNForContentSidebarEntryPoint$data } from './__generated__/GSNForContentSidebarEntryPoint.graphql.ts';

export function getInvitedAppShortcutsRecommendationsResult(
	growthRecommendations: GSNForContentSidebarEntryPoint$data['growthRecommendations'],
): InvitedAppShortcutsRecommendationsResult {
	const node = growthRecommendations?.recommendations;
	type GrowthRecommendations = NonNullable<
		InvitedAppShortcutsRecommendationsResponse['growthRecommendations']
	>;
	type Recommendations = GrowthRecommendations['recommendations'];

	let recommendations: Recommendations;
	if (node == null) {
		recommendations = null;
	} else if (node.__typename === 'GrowthRecRecommendations') {
		recommendations = {
			__typename: 'GrowthRecRecommendations',
			data: (node.data ?? []).map((item) => ({
				entityId: item?.entityId ?? '',
				reasons: item?.reasons ?? null,
				score: item?.score ?? null,
			})),
			context: node.context
				? {
						tenantId: node.context.tenantId ?? null,
						product: node.context.product ?? null,
						useCase: node.context.useCase ?? null,
						sessionId: node.context.sessionId ?? null,
					}
				: null,
		};
	} else if (node.__typename === 'QueryError') {
		recommendations = {
			__typename: 'QueryError',
			identifier: node.identifier ?? null,
			message: node.message ?? null,
			extensions: (node.extensions ?? []).map((ext) => ({
				statusCode: ext?.statusCode ?? null,
				errorType: ext?.errorType ?? null,
			})),
		};
	} else {
		// Unknown __typename — treat as no recommendations.
		recommendations = { __typename: node.__typename };
	}

	const response: InvitedAppShortcutsRecommendationsResponse = {
		growthRecommendations: { recommendations },
	};
	return buildInvitedAppShortcutsRecommendationsResult(response);
}
